import { INITIAL_SURVEY_CREATION_INSIGHTS_STATE } from './insights/survey-creation-insights-state.constants';
import {
  DEFAULT_SURVEY_LAUNCH_FORM,
  SurveyMarginFormState,
  EMPTY_ADVANCED_FEASIBILITY_FILTERS,
  INITIAL_LOCATION_UPLOAD_STATE,
  INITIAL_RESPONDENT_LIST_UPLOAD_STATE,
  SurveyLaunchFormState,
} from '@ps/survey-utils';

import {
  SurveyCreationLaunchState,
  QuestionCategoriesState,
  SurveyChildRegistryState,
  SurveyCreationAudienceState,
  SurveyCreationChildAudienceState,
  SurveyCreationCoreState,
  SurveyCreationReferenceState,
  SurveyCreationState,
  SurveyTagLinkState,
} from './survey-creation-state.model';
import { SuppliersMetaData } from './audience/survey-creation-suppliers-meta-data-state.model';
import { SurveyChildKey } from './survey-creation-shared-state.model';

export const INITIAL_QUESTION_CATEGORIES_STATE: QuestionCategoriesState = {
  data: [],
  cachedAt: null,
  loading: false,
};

export const INITIAL_TAG_LINK_STATE: SurveyTagLinkState = {
  linkedTagIds: [],
  loadingLinkedTags: false,
  selectedTagIds: [],
  linkTagsError: null,
};

export const INITIAL_SUPPLIERS_META_DATA_STATE: SuppliersMetaData = {
  blendId: '',
  priceOverride: null,
  defaultPurePriceOverride: false,
  isPurePriceRateCardEnable: false,
  supplierDefaultAllocation: false,
  surveyPauseThreshold: null,
  manualOverride: false,
};

// `valid: true` because a buyer without the Target Margin gate never fills this
// form in, and an untouched margin form must never be what blocks their save.
export const INITIAL_SURVEY_MARGIN_FORM_STATE: SurveyMarginFormState = {
  value: null,
  valid: true,
  errors: [],
  isDirty: false,
};

/** The empty-string key: no surveyId and no locale chosen yet. */
export const DEFAULT_SURVEY_CHILD_KEY: SurveyChildKey = '';

/** A fresh child's state. */
export const DEFAULT_CHILD_AUDIENCE_STATE: SurveyCreationChildAudienceState = {
  metadata: { value: null, valid: false, errors: [], isDirty: false },
  margin: { ...INITIAL_SURVEY_MARGIN_FORM_STATE },
  pendingHighCpiConfirmations: [],
  qualifications: { value: [], valid: true, errors: [], isDirty: false },
  quotas: { value: [], valid: true, errors: [], isDirty: false },
  locationFileUpload: { ...INITIAL_LOCATION_UPLOAD_STATE },
  advancedFeasibilityFilters: { ...EMPTY_ADVANCED_FEASIBILITY_FILTERS },
  tagLinks: { ...INITIAL_TAG_LINK_STATE },
  suppliersMetaData: { ...INITIAL_SUPPLIERS_META_DATA_STATE },
};

export const INITIAL_SURVEY_CREATION_AUDIENCE_STATE: SurveyCreationAudienceState =
  {
    children: {},
  };

/** A fresh child's registry entry. */
export const DEFAULT_CORE_CHILD_STATE: SurveyChildRegistryState = {
  survey: null,
  error: null,
  participant: null,
};

export const INITIAL_SURVEY_CREATION_CORE_STATE: SurveyCreationCoreState = {
  rootSurveyId: null,
  activeChildKey: null,
  kind: null,
  children: {
    [DEFAULT_SURVEY_CHILD_KEY]: DEFAULT_CORE_CHILD_STATE,
  },
  trackerConfig: null,
};

export const DEFAULT_CHILD_LAUNCH_STATE: SurveyLaunchFormState = {
  value: DEFAULT_SURVEY_LAUNCH_FORM,
  valid: false,
  errors: [],
  isDirty: false,
};

export const INITIAL_SURVEY_CREATION_LAUNCH_STATE: SurveyCreationLaunchState = {
  children: {
    [DEFAULT_SURVEY_CHILD_KEY]: DEFAULT_CHILD_LAUNCH_STATE,
  },
  pricing: null,
  isPricingLoading: false,
  isLaunching: false,
  aidaEligibility: null,
  respondentListUploads: {
    [DEFAULT_SURVEY_CHILD_KEY]: { ...INITIAL_RESPONDENT_LIST_UPLOAD_STATE },
  },
};

export const INITIAL_SURVEY_CREATION_REFERENCE_STATE: SurveyCreationReferenceState =
  {
    qualificationsRecord: {},
    questionCategories: { ...INITIAL_QUESTION_CATEGORIES_STATE },
    questionsRecord: {},
    questionsLoadingKey: null,
    locationConditionsRecord: {},
    locationConditionsLoadingKey: null,
  };

export const INITIAL_SURVEY_CREATION_STATE: SurveyCreationState = {
  core: { ...INITIAL_SURVEY_CREATION_CORE_STATE },
  audience: { ...INITIAL_SURVEY_CREATION_AUDIENCE_STATE },
  insights: { ...INITIAL_SURVEY_CREATION_INSIGHTS_STATE },
  reference: { ...INITIAL_SURVEY_CREATION_REFERENCE_STATE },
  launch: { ...INITIAL_SURVEY_CREATION_LAUNCH_STATE },
};
